"use client";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import { country } from "./country";
import styles from "./currencyFormat.module.css";

interface CountryItem {
  countryCode: string;
  countryName: string;
  currencyCode: string;
  population: string;
  capital: string;
  continentName: string;
}

interface InputData {
  amount?: number;
  from?: string;
  to?: string;
}

interface Errors {
  amount?: string;
  from?: string;
  to?: string;
}

interface CurrencyFormatProps {
  Data?: any;
}

interface SearchableCountrySelectProps {
  name: string;
  value: string;
  options: CountryItem[];
  onChange: (e: { target: { name: string; value: string } }) => void;
  placeholder?: string;
  hasError?: boolean;
}

function SearchableCountrySelect({
  name,
  value,
  options,
  onChange,
  placeholder = "Select Country",
  hasError,
}: SearchableCountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedLabel = useMemo(
    () => options.find((c) => c.currencyCode === value)?.countryName ?? "",
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (c) =>
        c.countryName.toLowerCase().includes(q) ||
        c.currencyCode.toLowerCase().includes(q)
    );
  }, [options, search]);

  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
    setHighlightedIndex(0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [close]);

  useEffect(() => {
    if (open) setHighlightedIndex(0);
  }, [open, search]);

  useEffect(() => {
    if (open && listRef.current && highlightedIndex >= 0) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [open, highlightedIndex]);

  const handleSelect = (item: CountryItem) => {
    onChange({ target: { name, value: item.currencyCode } });
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((i) => (i < filtered.length - 1 ? i + 1 : i));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((i) => (i > 0 ? i - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered[highlightedIndex]) handleSelect(filtered[highlightedIndex]);
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.searchableWrap} ${open ? styles.open : ""}`}
    >
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.open : ""} ${hasError ? styles.triggerError : ""} cursor-pointer`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selectedLabel || placeholder}
      >
        <span className={!selectedLabel ? styles.triggerPlaceholder : ""}>
          {selectedLabel || placeholder}
        </span>
      </button>
      <span className={styles.chevron} aria-hidden>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      {open && (
        <div className={styles.dropdown}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search country or currency..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") close();
              else e.stopPropagation();
            }}
            autoFocus
            aria-label="Search countries"
          />
          <ul
            ref={listRef}
            role="listbox"
            className={styles.list}
            aria-label="Countries"
          >
            {filtered.length === 0 ? (
              <li className={styles.noResults}>No results found</li>
            ) : (
              filtered.map((item, index) => (
                <li
                  key={item.currencyCode + item.countryCode}
                  role="option"
                  aria-selected={item.currencyCode === value}
                  className={index === highlightedIndex ? `${styles.item} ${styles.itemHighlighted}` : styles.item}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(item);
                  }}
                >
                  {item.countryName} ({item.currencyCode})
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const CurrencyFormat: React.FC<CurrencyFormatProps> = ({ Data }) => {
  const [inputData, setInputData] = useState<InputData>({});
  const [resultCurrency, setResultCurrency] = useState<number | null>(null);
  const [againGet, setAgainGet] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});

  const handleOnChangeAmount = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    // setValidationData(false);
    const { name, value } = e.target;

    setInputData((prev) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    if (againGet) {
      setAgainGet(false);
      handleONCurrency();
    }
  }, [againGet]);

  const handleONCurrency = async () => {
    if (validateForm()) {
      const { amount, from, to } = inputData;

      if (amount && from && to) {
        setLoading(true);

        try {
          const response = await fetch(
            `https://api.exchangerate-api.com/v4/latest/${from}`
          );
          const data = await response.json();

          if (data?.rates[to]) {
            const convertedAmount = data.rates[to] * amount;
            setResultCurrency(convertedAmount);
          } else {
            toast.error("Conversion rate not found for selected currencies.");
          }
        } catch (error) {
          toast.error("Something went wrong. Please try again.");
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const validateForm = (): boolean => {
    let formIsValid = true;
    const err: Errors = {};

    if (!inputData?.amount) {
      err.amount = "* Please enter Amount";
      formIsValid = false;
    }
    if (!inputData?.from) {
      err.from = "* Please select a country";
      formIsValid = false;
    }
    if (!inputData?.to) {
      err.to = "* Please select a country";
      formIsValid = false;
    }

    setErrors(err);
    return formIsValid;
  };

  return (
    <div>
      <div className="lenghth-box-design">
        <div className="two-col-grid">
          {/* From Section */}
          <div className="two-col-grid-items">
            <p>From :</p>
            <div className="all-input-design-alignment">
              <div>
                <input
                  type="number"
                  placeholder="1000"
                  name="amount"
                  onChange={handleOnChangeAmount}
                  className={errors?.amount ? "error-input" : ""}
                />
                <span className="text-sm text-red-700">{errors?.amount}</span>
              </div>
              <div>
                <SearchableCountrySelect
                  name="from"
                  value={inputData?.from ?? ""}
                  options={country as CountryItem[]}
                  onChange={handleOnChangeAmount}
                  placeholder="Select Country"
                  hasError={!!errors?.from}
                />
                <span className="text-sm text-red-700">{errors?.from}</span>
              </div>
            </div>
          </div>

          {/* To Section */}
          <div className="two-col-grid-items">
            <p>To :</p>
            <div className="all-input-design-alignment">
              <div>
                <input
                  type="text"
                  name="resultCurrency"
                  value={resultCurrency?.toFixed(2) || ""}
                  disabled={true}
                />
              </div>
              <div>
                <SearchableCountrySelect
                  name="to"
                  value={inputData?.to ?? ""}
                  options={country as CountryItem[]}
                  onChange={handleOnChangeAmount}
                  placeholder="Select Country"
                  hasError={!!errors?.to}
                />
                <span className="text-sm text-red-700">{errors?.to}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="button lowercase-center-alignment">
        <button type="submit" onClick={handleONCurrency} disabled={loading}>
          <span>Convert Currency</span>
          {loading && <Loader />}
        </button>
      </div>
    </div>
  );
};

export default CurrencyFormat;
